'use client';

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { sendMessageAction } from '../server/actions';
import type { MessageWithSender, SendMessageParams } from '../types';

/**
 * Paginated messages result for cache updates
 */
interface MessagesPageResult {
  messages: MessageWithSender[];
  nextCursor: string | null;
}

/**
 * Infinite query data structure for messages
 */
interface MessagesInfiniteData {
  pages: MessagesPageResult[];
  pageParams: (string | null)[];
}

interface UseSendMessageOptions {
  conversationId: string;
  onSuccess?: (message: MessageWithSender) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for sending messages with optimistic updates
 */
export function useSendMessage(options: UseSendMessageOptions) {
  const { conversationId, onSuccess, onError } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: Omit<SendMessageParams, 'conversationId'>) => {
      const {
        data: { user },
      } = await client.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      const response = await sendMessageAction({
        conversationId,
        content: params.content.trim(),
        contentType: params.contentType || 'text',
        attachments: params.attachments
          ? JSON.parse(JSON.stringify(params.attachments))
          : [],
        replyToId: params.replyToId || undefined,
        channel: params.channel || undefined,
        metadata: params.metadata ?? undefined,
      });

      if (!response.success) {
        throw new Error('Failed to send message');
      }

      const message = response.message;

      return {
        ...message,
        // BAL-580: an organization message has no sender id; the refetch
        // brings its org and author.
        sender: message.sender_id
          ? {
              id: message.sender_id,
              first_name: null,
              last_name: null,
              profile_photo_url: null,
            }
          : null,
        reply_to: null,
      } as MessageWithSender;
    },

    onMutate: async (params) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: ['chat-messages', conversationId],
      });

      // Get current user for optimistic update
      const {
        data: { user },
      } = await client.auth.getUser();

      // Generate temp ID for optimistic message
      const tempId = `temp-${Date.now()}`;

      // Create optimistic message
      const optimisticMessage = {
        id: tempId,
        conversation_id: conversationId,
        sender_id: user?.id || '',
        content: params.content.trim(),
        content_type: params.contentType || 'text',
        attachments: params.attachments
          ? JSON.parse(JSON.stringify(params.attachments))
          : [],
        reply_to_id: params.replyToId || null,
        is_deleted: false,
        is_edited: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        sender: {
          id: user?.id || '',
          first_name: user?.email?.split('@')[0] || 'You',
          last_name: null,
          profile_photo_url: null,
        },
        reply_to: null,
      } as MessageWithSender;

      // Snapshot for rollback
      const previousData = queryClient.getQueryData([
        'chat-messages',
        conversationId,
      ]);

      // Optimistically add message
      queryClient.setQueryData<MessagesInfiniteData>(
        ['chat-messages', conversationId],
        (oldData) => {
          if (!oldData?.pages?.[0]) return oldData;

          return {
            ...oldData,
            pages: [
              {
                ...oldData.pages[0],
                messages: [
                  optimisticMessage as MessageWithSender,
                  ...oldData.pages[0].messages,
                ],
              },
              ...oldData.pages.slice(1),
            ],
          };
        },
      );

      return { previousData, tempId };
    },

    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          ['chat-messages', conversationId],
          context.previousData,
        );
      }

      onError?.(error instanceof Error ? error : new Error(String(error)));
    },

    onSuccess: (data, _variables, context) => {
      // Replace optimistic message with real one
      queryClient.setQueryData<MessagesInfiniteData>(
        ['chat-messages', conversationId],
        (oldData) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  messages: page.messages.map((m) =>
                    m.id === context?.tempId ? data : m,
                  ),
                };
              }
              return page;
            }),
          };
        },
      );

      // Note: Realtime subscription handles chat-conversations and chat-total-unread-count
      // invalidation when the message INSERT event fires, so we skip redundant invalidation here.
      // Only invalidate specific conversation to update last_message_at optimistically
      queryClient.invalidateQueries({
        queryKey: ['chat-conversation', conversationId],
      });

      onSuccess?.(data);
    },
  });

  const send = useCallback(
    (params: Omit<SendMessageParams, 'conversationId'>) => {
      return mutation.mutateAsync(params);
    },
    [mutation],
  );

  return {
    send,
    sendMessage: mutation.mutate,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Hook for editing a message
 */
export function useEditMessage(conversationId: string) {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      content,
    }: {
      messageId: string;
      content: string;
    }) => {
      const { error } = await client
        .from('messages')
        .update({
          content: content.trim(),
          is_edited: true,
          edited_at: new Date().toISOString(),
        })
        .eq('id', messageId);

      if (error) {
        throw new Error(error.message);
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['chat-messages', conversationId],
      });
    },
  });
}

/**
 * Hook for deleting a message
 */
export function useDeleteMessage(conversationId: string) {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await client
        .from('messages')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          content: null,
        })
        .eq('id', messageId);

      if (error) {
        throw new Error(error.message);
      }
    },

    onMutate: async (messageId) => {
      await queryClient.cancelQueries({
        queryKey: ['chat-messages', conversationId],
      });

      const previousData = queryClient.getQueryData([
        'chat-messages',
        conversationId,
      ]);

      // Optimistically remove message
      queryClient.setQueryData<MessagesInfiniteData>(
        ['chat-messages', conversationId],
        (oldData) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.filter((m) => m.id !== messageId),
            })),
          };
        },
      );

      return { previousData };
    },

    onError: (_error, _messageId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['chat-messages', conversationId],
          context.previousData,
        );
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
  });
}
